
import React, { useEffect } from "react";
import { LeadAccount, StrategyProvider } from "@/types/strategy-types";
import LeadAccountFormDialog from "./LeadAccountFormDialog";
import DeleteLeadAccountDialog from "./DeleteLeadAccountDialog";
import EmptyLeadAccountsTable from "./EmptyLeadAccountsTable";
import LeadAccountsTableContent from "./LeadAccountsTableContent";
import LeadAccountTableFilters from "./LeadAccountTableFilters";
import { useLeadAccountsTable } from "@/hooks/admin/lead-accounts/useLeadAccountsTable";

interface LeadAccountsTableProps {
  leadAccounts: LeadAccount[];
  providers: StrategyProvider[];
  onCreateLeadAccount: (leadAccount: Omit<LeadAccount, 'id' | 'created_at' | 'updated_at'>) => Promise<void>;
  onUpdateLeadAccount: (id: string, leadAccount: Partial<LeadAccount>) => Promise<void>;
  onDeleteLeadAccount: (id: string) => Promise<void>;
  userRole?: string | null;
}

const LeadAccountsTable = ({
  leadAccounts,
  providers,
  onCreateLeadAccount,
  onUpdateLeadAccount,
  onDeleteLeadAccount,
  userRole
}: LeadAccountsTableProps) => {
  const {
    searchTerm,
    setSearchTerm,
    selectedProvider,
    setSelectedProvider,
    filteredLeadAccounts,
    selectedLeadAccount,
    isCreateDialogOpen,
    isEditDialogOpen,
    isDeleteDialogOpen,
    handleEdit,
    handleDelete,
    handleCreateLeadAccount,
    handleUpdateLeadAccount,
    handleDeleteLeadAccount,
    openCreateDialog,
    closeCreateDialog,
    closeEditDialog,
    closeDeleteDialog
  } = useLeadAccountsTable({
    leadAccounts,
    providers,
    onCreateLeadAccount,
    onUpdateLeadAccount,
    onDeleteLeadAccount
  });

  // Listen for custom events to open create dialog
  useEffect(() => {
    const handleOpenCreateDialog = () => {
      openCreateDialog();
    };

    window.addEventListener('openCreateLeadAccountDialog', handleOpenCreateDialog);
    
    return () => {
      window.removeEventListener('openCreateLeadAccountDialog', handleOpenCreateDialog);
    };
  }, []);
  
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold">Lead Accounts Management</h2>
          <p className="text-muted-foreground">Connect your strategies to exchange accounts</p>
        </div>
      </div>
      
      <LeadAccountTableFilters
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onAddNew={openCreateDialog}
        providers={userRole === 'admin' ? providers : undefined}
        selectedProvider={selectedProvider}
        onProviderChange={userRole === 'admin' ? setSelectedProvider : undefined}
      />
      
      {leadAccounts.length === 0 ? (
        <EmptyLeadAccountsTable />
      ) : (
        <LeadAccountsTableContent 
          filteredLeadAccounts={filteredLeadAccounts}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}
      
      <LeadAccountFormDialog
        isOpen={isCreateDialogOpen}
        onClose={closeCreateDialog}
        onSubmit={handleCreateLeadAccount}
        providers={providers}
        userRole={userRole}
      />
      
      {selectedLeadAccount && (
        <>
          <LeadAccountFormDialog
            isOpen={isEditDialogOpen}
            onClose={closeEditDialog}
            onSubmit={(data) => handleUpdateLeadAccount(selectedLeadAccount.id, data)}
            initialData={selectedLeadAccount}
            providers={providers}
            userRole={userRole}
          />
          
          <DeleteLeadAccountDialog
            isOpen={isDeleteDialogOpen}
            onClose={closeDeleteDialog}
            onConfirm={() => handleDeleteLeadAccount(selectedLeadAccount.id)}
          />
        </>
      )}
    </div>
  );
};

export default LeadAccountsTable;
